import React from "react";

import {
  resolveOptimusPlatformHeroImage,
  optimusPlatformHeroInitials,
} from "../utils/optimusPlatformHeroImages";

/*
 * A platform's circular hero mark and its name, as ONE identity unit.
 *
 * Used by every platform-keyed row in Plan vs Performance — Allocation
 * Adherence, KPI Attainment and the blended-eCPM decomposition — so the three
 * tables cannot drift into three treatments.
 *
 * ACCESSIBILITY. The image is decorative: the name sits right beside it, and a
 * screen reader announcing "YouTube YouTube" is worse than announcing it once.
 * Hence `alt=""` plus `aria-hidden`, and the fallback monogram is hidden the
 * same way — it is a visual stand-in for artwork, not a second label.
 *
 * LAYOUT. The frame is a fixed square with `flex: none`, so a row is the same
 * height before the image decodes as after it. That matters more than it
 * sounds: these tables are dense, and a late-arriving image that resizes its
 * row drags every row below it.
 */
export default function OptimusPlatformIdentity({ platform, label, className = "" }) {
  const { key, src, hasImage } = resolveOptimusPlatformHeroImage(platform);
  const text = label == null ? "" : String(label);

  /*
   * "All Platforms" is a SCOPE, not a channel, and campaign-scoped KPI rows
   * arrive with platform "all". Giving those a circle would assert an identity
   * that does not exist. The decision lives here rather than at each call site
   * so the three tables cannot answer it differently.
   */
  if (!key || key === "all") {
    return <span className={`optx-platform-identity ${className}`.trim()}>{text}</span>;
  }

  return (
    <span className={`optx-platform-identity ${className}`.trim()}>
      <span className="optx-platform-identity-frame" aria-hidden="true">
        {hasImage ? (
          <img
            className="optx-platform-identity-image"
            src={src}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            draggable="false"
          />
        ) : (
          /* Deliberate branded fallback — never a broken image, never a gap. */
          <span className="optx-platform-identity-fallback">
            {optimusPlatformHeroInitials(text, platform)}
          </span>
        )}
      </span>
      <span className="optx-platform-identity-label">{text}</span>
    </span>
  );
}
